/**
 * 训练会话持久化（IndexedDB）。阶段 2 目标：每组即时落库、页面刷新恢复未完成训练。
 * 阶段 3：createWorkout 接收规则引擎生成的计划（@jilian/core），不再使用静态演示计划；
 * 并新增「各动作最近重量」与「动作历史」查询（供生成/替换/进阶建议使用）。
 *
 * 数据模型沿用 @jilian/shared 的 WorkoutSession / WorkoutExercise / WorkoutSet。
 * 重量口径（WeightUnitScope）保存在每条组记录与动作上（decision 14.15），不做隐蔽换算。
 */
import { db } from "./db";
import { enqueueSyncEvent } from "./repo";
import type { GeneratedExercise, ReadinessResult } from "@jilian/core";
import type { WorkoutPlanType, RirValue, WeightUnitScope, WorkoutExercise, WorkoutSession, WorkoutSet, WorkoutExerciseStatus, WorkoutSessionStatus } from "@jilian/shared";
import type { WorkoutExerciseResult } from "@jilian/core";
import { uuid } from "../lib/ids";
import { currentTimezone, localDateString } from "../lib/timezone";

const nowIso = () => new Date().toISOString();
const DEVICE_ID = "device-demo-1";

export interface ActiveWorkout {
  session: WorkoutSession;
  items: WorkoutExercise[];
  sets: WorkoutSet[];
}

/** 新建训练会话：使用规则引擎生成的计划；持久化 planType 与 readinessSnapshot。 */
export async function createWorkout(
  sequence: string,
  userId: string,
  plan: GeneratedExercise[],
  readinessOrPlanType: ReadinessResult | WorkoutPlanType = "normal",
): Promise<ActiveWorkout> {
  const planType: WorkoutPlanType =
    typeof readinessOrPlanType === "string" ? readinessOrPlanType : (readinessOrPlanType?.planType ?? "normal");
  const readinessSnapshot = typeof readinessOrPlanType === "object" ? readinessOrPlanType : null;

  const session: WorkoutSession = {
    id: uuid(),
    userId,
    planId: null,
    deviceId: DEVICE_ID,
    startedAt: nowIso(),
    finishedAt: null,
    status: "in_progress",
    planType,
    readinessSnapshot,
    overallDifficulty: null,
    footStatusAfter: null,
    jointIssueAfter: null,
    notes: `sequence=${sequence}`,
    createdOffline: true,
    syncStatus: "local_only",
    timezoneAtStart: currentTimezone(),
    localDateAtStart: localDateString(),
    createdAt: nowIso(),
    updatedAt: nowIso(),
  };

  const items: WorkoutExercise[] = plan.map((p, index) => ({
    id: uuid(),
    sessionId: session.id,
    exerciseId: p.exercise.id,
    exerciseVersion: p.exercise.version,
    orderIndex: index,
    suggestedWeightKg: p.suggestedWeightKg,
    actualBaseWeightKg: null,
    weightUnitScope: p.weightUnitScope,
    targetSets: p.targetSets,
    repMin: p.repMin,
    repMax: p.repMax,
    targetRir: p.targetRir as RirValue,
    restSeconds: p.restSeconds,
    status: "pending",
    replacementForId: null,
    replacementReason: null,
    skipReason: null,
    progressionDecisionId: null,
    createdAt: nowIso(),
    updatedAt: nowIso(),
  }));

  const sets: WorkoutSet[] = items.flatMap((item) =>
    Array.from({ length: item.targetSets }, (_, i) => ({
      id: uuid(),
      workoutExerciseId: item.id,
      setIndex: i + 1,
      suggestedWeightKg: item.suggestedWeightKg,
      actualWeightKg: item.suggestedWeightKg,
      weightUnitScope: item.weightUnitScope,
      reps: item.repMin,
      rir: item.targetRir,
      reachedFailure: false,
      muscleDiscomfort: false,
      jointPain: false,
      injuryPain: false,
      formBreakdown: false,
      restStartedAt: null,
      restTargetEndAt: null,
      actualRestSeconds: null,
      completedAt: null,
      status: "planned",
      unplannedFailure: false,
      version: 1,
      createdAt: nowIso(),
      updatedAt: nowIso(),
    })),
  );

  await db.transaction("rw", db.sessions, db.exerciseItems, db.sets, async () => {
    await db.sessions.put(session);
    await db.exerciseItems.bulkPut(items);
    await db.sets.bulkPut(sets);
  });

  await enqueueSyncEvent({ userId, entityType: "workout_session", entityId: session.id, operation: "upsert", entityVersion: 1, payload: session });
  return { session, items, sets };
}

/** 读取最近一次「进行中」的训练会话（跨会话取最新）。 */
export async function loadActiveWorkout(): Promise<ActiveWorkout | null> {
  const rows = await db.sessions.where("status").equals("in_progress").sortBy("startedAt");
  const session = rows[rows.length - 1];
  if (!session) return null;
  const items = await db.exerciseItems.where("sessionId").equals(session.id).sortBy("orderIndex");
  const itemIds = items.map((i) => i.id);
  const sets = itemIds.length ? await db.sets.where("workoutExerciseId").anyOf(itemIds).toArray() : [];
  return { session, items, sets };
}

export async function saveSet(set: WorkoutSet, userId: string): Promise<void> {
  await db.sets.put(set);
  await enqueueSyncEvent({ userId, entityType: "workout_set", entityId: set.id, operation: "upsert", entityVersion: set.version, payload: set });
}

export async function saveExerciseItem(item: WorkoutExercise, userId: string): Promise<void> {
  await db.exerciseItems.put(item);
  await enqueueSyncEvent({ userId, entityType: "workout_exercise", entityId: item.id, operation: "upsert", entityVersion: item.exerciseVersion, payload: item });
}

export async function finishSession(
  sessionId: string,
  status: WorkoutSessionStatus,
  userId: string,
  notes?: string,
): Promise<void> {
  const existing = await db.sessions.get(sessionId);
  const updatedNotes = notes ? `${existing?.notes ? `${existing.notes};` : ""}endReason=${notes}` : existing?.notes ?? null;
  const update = {
    status,
    notes: updatedNotes,
    finishedAt: nowIso(),
    updatedAt: nowIso(),
  } as Partial<WorkoutSession>;
  await db.sessions.update(sessionId, update);
  const session = await db.sessions.get(sessionId);
  if (session) {
    await enqueueSyncEvent({ userId, entityType: "workout_session", entityId: sessionId, operation: "upsert", entityVersion: 2, payload: { ...session, ...update } });
  }
}

/**
 * 各动作最近一次使用重量（阶段 3：生成建议 / 替换历史展示 / 初始建议覆盖）。
 * 口径随记录保存（决策 14.15），不同器械版本视为不同记录，不混算。
 */
export async function loadLatestWeights(
  userId: string,
): Promise<Map<string, { weightKg: number; weightUnitScope: WeightUnitScope }>> {
  const sessions = await db.sessions.where("userId").equals(userId).toArray();
  const sessionIds = sessions.map((s) => s.id);
  if (sessionIds.length === 0) return new Map();
  const items = await db.exerciseItems.where("sessionId").anyOf(sessionIds).toArray();
  const itemIds = items.map((i) => i.id);
  if (itemIds.length === 0) return new Map();
  const sets = await db.sets.where("workoutExerciseId").anyOf(itemIds).toArray();
  const byItem = new Map<string, WorkoutExercise>();
  for (const it of items) byItem.set(it.id, it);

  const latest = new Map<string, { weightKg: number; weightUnitScope: WeightUnitScope; at: string }>();
  for (const s of sets) {
    if (s.status !== "completed" || s.actualWeightKg == null) continue;
    const item = byItem.get(s.workoutExerciseId);
    if (!item) continue;
    const at = s.completedAt ?? s.updatedAt;
    const prev = latest.get(item.exerciseId);
    if (!prev || at > prev.at) {
      latest.set(item.exerciseId, { weightKg: s.actualWeightKg, weightUnitScope: s.weightUnitScope, at });
    }
  }
  return new Map(
    [...latest.entries()].map(([id, v]) => [id, { weightKg: v.weightKg, weightUnitScope: v.weightUnitScope }]),
  );
}

/**
 * 同一动作的历史表现（进阶建议用；已按「同口径」过滤交给规则引擎）。
 * 读取各历史会话真实 planType；轻/减量日历史在规则引擎中不计入连续失败。
 */
export async function loadExerciseHistory(
  exerciseId: string,
  excludeSessionId: string,
): Promise<WorkoutExerciseResult[]> {
  const items = await db.exerciseItems.where("exerciseId").equals(exerciseId).toArray();
  const itemsOfOther = items.filter((i) => i.sessionId !== excludeSessionId);
  if (itemsOfOther.length === 0) return [];

  const sessionIds = Array.from(new Set(itemsOfOther.map((i) => i.sessionId)));
  const sessions = await db.sessions.where("id").anyOf(sessionIds).toArray();
  const sessionMap = new Map(sessions.map((s) => [s.id, s]));

  const results: WorkoutExerciseResult[] = [];
  for (const item of itemsOfOther) {
    const sess = sessionMap.get(item.sessionId);
    const sets = await db.sets
      .where("workoutExerciseId")
      .equals(item.id)
      .toArray()
      .then((list) => list.filter((s) => s.status === "completed").sort((a, b) => a.setIndex - b.setIndex));
    if (sets.length === 0) continue;
    const completed = sets.filter((s) => s.reps != null && s.rir != null);
    const last = sets[sets.length - 1]!;
    results.push({
      exerciseId: item.exerciseId,
      weightKg: last.actualWeightKg ?? null,
      weightUnitScope: item.weightUnitScope,
      targetSets: item.targetSets,
      repMin: item.repMin,
      repMax: item.repMax,
      targetRir: item.targetRir,
      failureAllowedLastSet: false, // 从动作库版本回填
      hasCompleteData: completed.length === item.targetSets,
      jointPain: sets.some((s) => s.jointPain),
      injuryPain: sets.some((s) => s.injuryPain),
      formBreakdown: sets.some((s) => s.formBreakdown),
      planType: sess?.planType ?? "normal",
      restShort: sets.some((s) => s.actualRestSeconds != null && s.actualRestSeconds < item.restSeconds * 0.6),
      skipped: item.status === "skipped",
      endedEarly: item.status === "ended_early",
      sets: sets.map((s, i) => ({ reps: s.reps ?? 0, rir: (s.rir ?? 4) as RirValue, isLast: i === sets.length - 1 })),
    });
  }
  return results;
}

export { DEVICE_ID };
export type { WorkoutExerciseStatus };
